import { useEffect } from "react";
import { pinApi } from "../services/pin.api";

/** Minimum delay between two pings. */
export const ACTIVITY_PING_INTERVAL_MS = 60 * 1000;
/** How often the conditions are checked. */
export const ACTIVITY_CHECK_INTERVAL_MS = 10 * 1000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const;

/**
 * MED-1621: the server locks the session after 180 s without an API call, even when the user is
 * reading a page or typing a long form. While `enabled` (unlocked session with a PIN), ping
 * POST /account/activity when there was user activity since the last ping, at least 60 s have
 * passed and the tab is visible. Fire-and-forget: a 423 locks through the API client.
 */
export const useActivityHeartbeat = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;

    // The call that unlocked / validated the session counts as the last activity.
    let lastPing = Date.now();
    let active = false;

    const onActivity = () => {
      active = true;
    };
    const check = () => {
      if (!active || document.visibilityState !== "visible") return;
      if (Date.now() - lastPing < ACTIVITY_PING_INTERVAL_MS) return;
      lastPing = Date.now();
      active = false;
      pinApi.activity().catch(() => undefined);
    };

    ACTIVITY_EVENTS.forEach((name) =>
      document.addEventListener(name, onActivity, { capture: true, passive: true })
    );
    const timer = window.setInterval(check, ACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
      ACTIVITY_EVENTS.forEach((name) =>
        document.removeEventListener(name, onActivity, { capture: true })
      );
    };
  }, [enabled]);
};
